/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { backendPropertiesCrud } from '@services/housien';
import { uploadPropertyFileImages } from '@services/images-upload';
import { getValidAuthToken } from '@services/auth/auth';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry } from '@shared/utils/with-antibot-retry';
import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { transformToRequest } from './utils';
import type { PropertyFormValues } from './schema';

const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

const VALIDATION_FIELD_LABEL_KEYS: Record<string, string> = {
  category: 'form.fieldLabels.propertyType', transaction_type: 'form.fieldLabels.purpose',
  postal_code: 'form.fieldLabels.postalCode', street: 'form.fieldLabels.street', number: 'form.fieldLabels.number',
  neighborhood: 'form.fieldLabels.neighborhood', city: 'form.fieldLabels.city', state: 'form.fieldLabels.state',
  total_area: 'form.fieldLabels.totalArea', constructed_area: 'form.fieldLabels.constructedArea',
  sale_price: 'form.fieldLabels.salePrice', rental_price: 'form.fieldLabels.rentalPrice',
  condominium_price: 'form.fieldLabels.condoPrice', iptu: 'form.fieldLabels.iptu', property_title: 'form.rightPanel.adTitle',
};

/**
 * Scrolls the first field carrying a validation error into view (by `name` or
 * `data-field` attribute), no-op when no error key is given.
 *
 * @param firstErrorKey - Field key of the first error, if any.
 */
function scrollToFirstError(firstErrorKey: string | undefined): void {
  if (!firstErrorKey) return;
  const el =
    document.querySelector(`[name="${firstErrorKey}"]`) ?? document.querySelector(`[data-field="${firstErrorKey}"]`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Builds a localized validation message listing the labeled fields currently in
 * error and scrolls to the first one.
 *
 * @param methods - React Hook Form methods holding the form state.
 * @param t - Translation function for localized labels/messages.
 * @returns The composed validation error message.
 */
function collectValidationErrors(methods: UseFormReturn<PropertyFormValues>, t: TFunction): string {
  const fieldErrors = methods.formState.errors;
  const errorFields = Object.keys(fieldErrors).map((key) => {
    const labelKey = VALIDATION_FIELD_LABEL_KEYS[key];
    return labelKey ? t(labelKey) : key;
  });
  const msg =
    errorFields.length > 0
      ? t('form.validation.fieldsWithError', { fields: errorFields.join(', ') })
      : t('form.validation.fillRequired');
  scrollToFirstError(Object.keys(fieldErrors)[0]);
  return msg;
}

/**
 * Extracts the first identifier from a plural list, falling back to a singular
 * value when the list is absent.
 *
 * @param plural - Optional list of identifiers.
 * @param singular - Optional single identifier fallback.
 * @returns The first identifier, or `undefined` when none exist.
 */
function firstId(plural: string[] | undefined, singular: string | undefined): string | undefined {
  const list = plural ?? (singular ? [singular] : []);
  return list[0];
}

/**
 * Builds the publication update body for a single portal, skipping portals that
 * are absent or in an error state.
 *
 * @param portal - Portal slug.
 * @param pub - The portal publication record.
 * @returns The update body, or `null` to skip the portal.
 */
function buildPortalBody(portal: string, pub: any): Record<string, string> | null {
  // An OLX ad is rebuilt from the property, so a failed one (e.g. no photos)
  // is re-sent too: the edit may be exactly what fixes it.
  if (!pub || (pub.status === 'error' && portal !== 'olx')) return null;
  const body: Record<string, string> = {};
  const postId = firstId(pub.post_ids, pub.post_id);
  const listingId = firstId(pub.listing_ids, pub.listing_id);
  if (postId) body.post_id = postId;
  if (listingId) body.listing_id = listingId;
  return body;
}

/**
 * Sends a single portal publication update with a fresh anti-bot token.
 *
 * @param agencyId - Owning agency identifier.
 * @param propertyId - Property identifier.
 * @param portal - Portal slug.
 * @param pub - The portal publication record.
 * @returns A promise resolving when the update completes or is skipped.
 */
function publishPortal(
  agencyId: string, propertyId: string, portal: string, pub: unknown,
): Promise<unknown> {
  const body = buildPortalBody(portal, pub);
  if (!body) return Promise.resolve();
  return withAntiBotRetry(
    (headers) =>
      axios.put(
        `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publications/${portal}`,
        body,
        { headers },
      ),
    (opts) => buildPortalIntegrationHeaders('portal_sync', opts),
  );
}

/**
 * Resolves auth-only headers for safe GET portal-status calls (no anti-bot).
 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getValidAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Fetches the current portal publication status map for a property, returning
 * an empty map when the request fails.
 *
 * @param agencyId - Owning agency identifier.
 * @param propertyId - Property identifier.
 * @param headers - Authenticated request headers.
 * @returns The per-portal publication status map.
 */
async function fetchPortalStatus(
  agencyId: string, propertyId: string, headers: Record<string, string>
): Promise<Record<string, any>> {
  const statusRes = await axios.get(
    `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propertyId}/publish-status`,
    { headers },
  ).catch(() => null);
  return statusRes?.data?.portals ?? {};
}

/**
 * Re-syncs every active portal publication for a property after it is saved,
 * surfacing success or error via toast/error reporting.
 *
 * @param agencyId - Owning agency identifier.
 * @param propertyId - Property identifier.
 * @param t - Translation function for localized messages.
 */
export async function syncPortalPublications(agencyId: string, propertyId: string, t: TFunction) {
  try {
    const headers = await authHeaders();
    const portals = await fetchPortalStatus(agencyId, propertyId, headers);
    await Promise.allSettled(
      Object.entries(portals).map(([portal, pub]) =>
        publishPortal(agencyId, propertyId, portal, pub),
      ),
    );
    toast.success(t('formDualPane.rightPanel.syncSuccess'));
  } catch (error) {
    reportError(error, { message: t('formDualPane.rightPanel.syncError') });
  }
}

const INVALID_FIELD_LABEL_KEYS: Record<string, string> = {
  city: 'form.fieldLabels.city', neighborhood: 'form.fieldLabels.neighborhood', number: 'form.fieldLabels.number',
  postal_code: 'form.fieldLabels.postalCode', property_title: 'form.rightPanel.adTitle', state: 'form.fieldLabels.state',
  street: 'form.fieldLabels.street', category: 'form.fieldLabels.propertyType', transaction_type: 'form.fieldLabels.purpose',
};

/**
 * Formats a backend invalid-properties map into a newline-separated,
 * human-readable detail string using localized field labels.
 *
 * @param body - Map of field name to validation messages.
 * @param t - Translation function for localized labels.
 * @returns The formatted detail string.
 */
function formatInvalidProperties(body: Record<string, unknown>, t: TFunction): string {
  return Object.entries(body)
    .map(([field, msgs]) => {
      const labelKey = INVALID_FIELD_LABEL_KEYS[field];
      const label = labelKey ? t(labelKey) : field;
      return `${label}: ${(msgs as string[]).join(', ')}`;
    })
    .join('\n');
}

/**
 * Extracts the invalid-properties map from a submit error, checking both the
 * response and error shapes.
 *
 * @param error - The submit error to inspect.
 * @returns The invalid-properties map, or `null` when absent.
 */
function extractInvalidProperties(error: any): Record<string, unknown> | null {
  const response = error?.response ?? error?.data;
  const body = response?.invalid_properties ?? error?.invalid_properties;
  return body && typeof body === 'object' ? body : null;
}

/**
 * Derives a user-facing detail message from a property submit error, preferring
 * the invalid-properties breakdown over the raw error message.
 *
 * @param error - The submit error to inspect.
 * @param t - Translation function for localized fallbacks.
 * @returns The detail message to display.
 */
function buildSubmitErrorDetail(error: any, t: TFunction): string {
  const body = extractInvalidProperties(error);
  if (body) return formatInvalidProperties(body, t);
  return error?.message ?? t('form.validation.unknownError');
}

interface SubmitParams {
  methods: UseFormReturn<PropertyFormValues>;
  t: TFunction;
  agency: any;
  editingProperty?: Record<string, any> | null;
  pendingImagesRef: React.MutableRefObject<File[]>;
  syncPublications: boolean;
  onSuccess?: () => void;
}

/**
 * Persists the current form values, updating when an existing property id is
 * present or inserting otherwise, and returns the resulting property id.
 *
 * @param methods - React Hook Form methods holding the form values.
 * @param agency - The active agency context.
 * @param editingProperty - Existing property when editing, else null/undefined.
 * @returns The persisted property id, or `undefined` when unavailable.
 */
async function persistProperty(
  methods: UseFormReturn<PropertyFormValues>, agency: any, editingProperty?: Record<string, any> | null
): Promise<string | undefined> {
  const requestData = transformToRequest(methods.getValues());
  const existingId = editingProperty?.id;
  if (existingId) {
    await backendPropertiesCrud(agency.id).update({ id: existingId, body: requestData });
    return existingId;
  }
  const result = (await backendPropertiesCrud(agency.id).insert({ body: requestData })) as any;
  return result?.message ?? result?.detail ?? result?.id ?? result?.data?.id;
}

interface UploadContext {
  pendingImagesRef: React.MutableRefObject<File[]>;
  agency: any;
  t: TFunction;
  setSubmitError: (msg: string) => void;
}

/**
 * Reports the missing-property-id upload failure (console + inline + toast) and
 * returns the failure flag.
 *
 * @param ctx - Upload context carrying the setter and translator.
 * @returns Always `true` to signal upload failure.
 */
function reportMissingId(ctx: UploadContext): boolean {
  console.error('[PropertyForm] Cannot upload images: propertyId not available after creation');
  ctx.setSubmitError(ctx.t('form.validation.imageUploadNoId'));
  reportError(undefined, { message: ctx.t('form.validation.imageUploadError', { detail: 'ID' }), autoClose: 10000 });
  return true;
}

/**
 * Reports an image upload failure (console + inline + toast) and returns the
 * failure flag.
 *
 * @param ctx - Upload context carrying the setter and translator.
 * @param imgErr - The image upload error.
 * @returns Always `true` to signal upload failure.
 */
function reportUploadFailure(ctx: UploadContext, imgErr: any): boolean {
  console.error('[PropertyForm] image upload error:', imgErr);
  const detail = imgErr?.message ?? ctx.t('form.validation.unknownError');
  ctx.setSubmitError(ctx.t('form.validation.dataSavedPhotoError', { detail }));
  reportError(imgErr, { message: ctx.t('form.validation.imageUploadError', { detail }), autoClose: 10000 });
  return true;
}

/**
 * Uploads pending images for the saved property, short-circuiting when there
 * are none and handling missing-id and upload-error cases.
 *
 * @param propertyId - The persisted property id, if available.
 * @param ctx - Upload context (pending files, agency, translator, setter).
 * @returns True when the upload failed (blocking success), false otherwise.
 */
async function uploadImages(propertyId: string | undefined, ctx: UploadContext): Promise<boolean> {
  if (ctx.pendingImagesRef.current.length === 0) return false;
  if (!propertyId) return reportMissingId(ctx);
  try {
    await uploadPropertyFileImages(ctx.pendingImagesRef.current, propertyId, ctx.agency.id);
    return false;
  } catch (imgErr: any) {
    return reportUploadFailure(ctx, imgErr);
  }
}

/**
 * Triggers React Hook Form validation, reporting the collected errors and
 * returning false when validation fails.
 *
 * @param methods - React Hook Form methods holding the form state.
 * @param t - Translation function for localized messages.
 * @param setSubmitError - Setter receiving the validation error message.
 * @returns True when validation passes, false otherwise.
 */
async function runValidation(
  methods: UseFormReturn<PropertyFormValues>, t: TFunction, setSubmitError: (m: string) => void
): Promise<boolean> {
  if (await methods.trigger()) return true;
  const msg = collectValidationErrors(methods, t);
  setSubmitError(msg);
  reportError(undefined, { message: msg, autoClose: 6000 });
  return false;
}

interface PersistContext {
  methods: UseFormReturn<PropertyFormValues>;
  t: TFunction;
  agency: any;
  editingProperty?: Record<string, any> | null;
  pendingImagesRef: React.MutableRefObject<File[]>;
  syncPublications: boolean;
  onSuccess?: () => void;
  setSaved: (v: boolean) => void;
  setSubmitError: (m: string) => void;
}

/**
 * Re-syncs portal publications when the sync flag is enabled and both the
 * property id and agency id are known; otherwise a no-op.
 *
 * @param ctx - Persist context carrying the sync flag, agency, and translator.
 * @param propertyId - The persisted property id, if available.
 */
async function maybeSyncPublications(ctx: PersistContext, propertyId: string | undefined): Promise<void> {
  const canSync = ctx.syncPublications && propertyId && ctx.agency?.id;
  if (!canSync) return;
  await syncPortalPublications(ctx.agency.id, propertyId as string, ctx.t);
}

const RECOVERY_KEY = 'property-form-recovery';

/**
 * Persists the property, uploads pending images, optionally re-syncs portals,
 * and finalizes success side effects (saved flag + onSuccess callback).
 *
 * @param ctx - Persist context with form methods, agency, refs, and setters.
 */
async function persistAndUpload(ctx: PersistContext): Promise<void> {
  sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(ctx.methods.getValues()));
  const propertyId = await persistProperty(ctx.methods, ctx.agency, ctx.editingProperty);
  sessionStorage.removeItem(RECOVERY_KEY);
  const uploadCtx: UploadContext = {
    pendingImagesRef: ctx.pendingImagesRef, agency: ctx.agency, t: ctx.t, setSubmitError: ctx.setSubmitError,
  };
  if (await uploadImages(propertyId, uploadCtx)) return;
  await maybeSyncPublications(ctx, propertyId);
  ctx.setSaved(true);
  ctx.onSuccess?.();
}

/**
 * Restores form values from sessionStorage if recovery data exists (e.g.
 * after a 428 anti-bot page reload). Returns `true` when restoration
 * occurred so the caller can skip its default initialization.
 *
 * Call this on form mount.
 */
export function restoreFormFromRecovery(methods: UseFormReturn<PropertyFormValues>): boolean {
  const raw = sessionStorage.getItem(RECOVERY_KEY);
  if (!raw) return false;
  try {
    const values = JSON.parse(raw) as PropertyFormValues;
    methods.reset(values);
    sessionStorage.removeItem(RECOVERY_KEY);
    return true;
  } catch {
    sessionStorage.removeItem(RECOVERY_KEY);
    return false;
  }
}

/**
 * Reports a top-level submit failure to the user (inline + toast) and logs it
 * to the console.
 *
 * @param error - The submit error.
 * @param t - Translation function for localized fallbacks.
 * @param setSubmitError - Setter receiving the detail message.
 */
function reportSubmitFailure(error: any, t: TFunction, setSubmitError: (m: string) => void): void {
  const detail = buildSubmitErrorDetail(error, t);
  setSubmitError(detail);
  reportError(error, { message: detail, autoClose: 8000 });
  console.error('[PropertyForm] submit error:', error);
}

/**
 * Drives the property form submit lifecycle: validation, create/update
 * persistence, image upload, optional portal re-sync, and error surfacing.
 *
 * @param params - Form methods, translator, agency, optional editing property,
 *   pending-images ref, publication-sync flag, and success callback.
 * @returns `saved`/`submitting`/`submitError` state, the `isEdit` flag, and the
 *   `handleSubmit` action.
 */
export function usePropertyFormSubmit(
  { methods, t, agency, editingProperty, pendingImagesRef, syncPublications, onSuccess }: SubmitParams
) {
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEdit = !!editingProperty;

  async function handleSubmit() {
    setSubmitError(null);
    if (!(await runValidation(methods, t, setSubmitError))) return;
    const ctx: PersistContext = {
      methods, t, agency, editingProperty, pendingImagesRef, syncPublications, onSuccess, setSaved, setSubmitError,
    };
    try {
      setSubmitting(true);
      await persistAndUpload(ctx);
    } catch (error: any) {
      reportSubmitFailure(error, t, setSubmitError);
    } finally {
      setSubmitting(false);
    }
  }

  return { saved, submitting, submitError, isEdit, handleSubmit };
}
